import { prefersReducedMotion, useHostHover } from "./useHostHover";

const EASE = "cubic-bezier(0.16, 1, 0.3, 1)";

/** Back bubble (upper left), centreline of the Chats.svg outline. Tail tip: 2.25, 11.85 */
const BUBBLE_BACK =
  "M3.375 2.8125H11.25A1.125 1.125 0 0 1 12.375 3.9375V9A1.125 1.125 0 0 1 11.25 10.125H5.5L2.25 11.85V3.9375A1.125 1.125 0 0 1 3.375 2.8125Z";

/** Front bubble (lower right), centreline. Tail tip: 15.75, 15.4 */
const BUBBLE_FRONT =
  "M6.75 6.1875H14.625A1.125 1.125 0 0 1 15.75 7.3125V15.4L12.4 13.5H6.75A1.125 1.125 0 0 1 5.625 12.375V7.3125A1.125 1.125 0 0 1 6.75 6.1875Z";

export type ChatIconProps = {
  size?: number;
  /** Icon stroke colour. Defaults to puntt-blue-12. */
  color?: string;
  /** Surface colour — fills each bubble so the front one occludes the back one. */
  bg?: string;
  /** Animation duration in ms. */
  duration?: number;
  /** Swing angle in degrees. */
  angle?: number;
  className?: string;
  /** Drive hover from the parent. Omit to follow the host `<a>` / `<button>`. */
  hover?: boolean;
  "data-testid"?: string;
};

/**
 * Chats with Puntt (Phosphor Chats, split into two z-orderable halves).
 * Hover: each bubble pivots about its own tail tip in opposite directions
 * (back CCW, front CW) and the stack order flips, so one card rotates out
 * from behind the other. Reverses on exit.
 *
 * Two layers hold the two stacking orders. The opacity swap is a 50ms cut
 * timed to 14% of the duration — the point where both layers sit at the
 * same angle — so the crossfade is invisible. Do not lengthen it into a fade.
 */
export function ChatIcon({
  size = 18,
  color = "rgb(var(--puntt-blue-12))",
  bg = "#ffffff",
  duration = 340,
  angle = 18,
  className,
  hover: hoverProp,
  "data-testid": testId,
}: ChatIconProps) {
  const { ref, hover } = useHostHover(hoverProp);
  const reduceMotion = prefersReducedMotion();
  const motionMs = reduceMotion ? 0 : duration;
  const swing = reduceMotion ? 0 : angle;

  const cut = {
    transition: `opacity ${reduceMotion ? 0 : 50}ms linear ${
      reduceMotion ? 0 : Math.round(duration * 0.14)
    }ms`,
  } as const;
  const spin = (origin: string, deg: number) =>
    ({
      transformBox: "view-box",
      transformOrigin: origin,
      transition: `transform ${motionMs}ms ${EASE}`,
      transform: `rotate(${deg}deg)`,
    }) as const;

  const backOrigin = "2.25px 11.85px";
  const frontOrigin = "15.75px 15.4px";

  // why: block + line-height 0 — inline-flex inherited the size-3 24px line-height and made these rows 6.5px taller than New, sitting the glyph high
  return (
    <span
      ref={ref}
      className={className}
      data-testid={testId}
      data-hovered={hover ? "true" : "false"}
      aria-hidden="true"
      style={{
        display: "block",
        width: size,
        height: size,
        lineHeight: "0",
      }}
    >
      <svg
        viewBox="0 0 18 18"
        width={size}
        height={size}
        fill="none"
        stroke={color}
        strokeWidth="1.125"
        strokeLinejoin="round"
        strokeLinecap="round"
        style={{ display: "block" }}
      >
        {/* rest order: back bubble behind, front bubble on top */}
        <g data-stack="rest" style={{ ...cut, opacity: hover ? 0 : 1 }}>
          <path
            d={BUBBLE_BACK}
            fill={bg}
            data-bubble="back"
            style={spin(backOrigin, hover ? -swing : 0)}
          />
          <path
            d={BUBBLE_FRONT}
            fill={bg}
            data-bubble="front"
            style={spin(frontOrigin, hover ? swing : 0)}
          />
        </g>

        {/* hover order: swapped */}
        <g data-stack="hover" style={{ ...cut, opacity: hover ? 1 : 0 }}>
          <path
            d={BUBBLE_FRONT}
            fill={bg}
            style={spin(frontOrigin, hover ? 0 : swing)}
          />
          <path
            d={BUBBLE_BACK}
            fill={bg}
            style={spin(backOrigin, hover ? 0 : -swing)}
          />
        </g>
      </svg>
    </span>
  );
}
